
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Lead, LeadStatus, LeadPriority, LeadEnrichmentResult } from "@/components/leads/types";

export function useLeads(filters?: {
  status?: string;
  priority?: string;
  searchQuery?: string;
}) {
  return useQuery({
    queryKey: ["leads", filters],
    queryFn: async () => {
      console.log("Fetching leads with filters:", filters);
      
      let query = supabase
        .from("lead_master")
        .select("*")
        .order("updated_at", { ascending: false });
      
      if (filters?.status && filters.status !== "all" && filters.status !== "") {
        query = query.eq("status", filters.status as LeadStatus);
      }
      
      if (filters?.priority && filters.priority !== "all" && filters.priority !== "") {
        query = query.eq("priority", filters.priority as LeadPriority);
      }
      
      if (filters?.searchQuery) {
        query = query.textSearch("search_vector", filters.searchQuery);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error fetching leads:", error);
        toast.error("Failed to fetch leads");
        throw error;
      }
      
      console.log(`Successfully fetched ${data?.length || 0} leads`);
      return data as Lead[];
    },
    staleTime: 5 * 60 * 1000, 
    retry: 1
  });
}

export function useLead(id?: string) {
  return useQuery<Lead, Error>({
    queryKey: ["lead", id],
    queryFn: async () => {
      if (!id) throw new Error("Lead ID is required");
      
      console.log("Fetching lead details for ID:", id);
      
      const { data: leadData, error: leadError } = await supabase
        .from("lead_master")
        .select(`
          *,
          contacts:lead_contacts(*),
          tags:lead_tags(*),
          activities:lead_activities(*),
          attachments:lead_attachments(*),
          news_alerts(*)
        `)
        .eq("id", id)
        .single();
      
      if (leadError) {
        console.error("Error fetching lead_master:", leadError);
        toast.error(`Failed to fetch lead details: ${leadError.message}`);
        throw leadError;
      }

      if (!leadData) {
        throw new Error("Lead not found.");
      }

      // Fetch the latest lead enrichment result
      const { data: enrichmentData, error: enrichmentError } = await supabase
        .from("lead_enrichment_results")
        .select("*")
        .eq("lead_id", id)
        .order("requested_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (enrichmentError) {
        console.warn("Error fetching lead enrichment data:", enrichmentError.message);
      }
      
      console.log(`Lead ${id} fetched. Enrichment data:`, enrichmentData);

      return {
        ...leadData,
        latest_enrichment_result: enrichmentData as LeadEnrichmentResult | null,
      } as Lead;
    },
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
    retry: 1
  });
}

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, status }: { leadId: string; status: LeadStatus }) => {
      const { error } = await supabase
        .from("lead_master")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", leadId);

      if (error) {
        console.error("Error updating lead status:", error);
        throw error;
      }
      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (error) => {
      toast.error(`Failed to update lead status: ${error.message}`);
    },
  });
}

export function useApplyEnrichmentToLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, enrichedData }: { leadId: string; enrichedData: Partial<Lead> }) => {
      console.log(`Applying enrichment data to lead ${leadId}:`, enrichedData);
      const dataToUpdate = {
        ...enrichedData,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("lead_master")
        .update(dataToUpdate)
        .eq("id", leadId)
        .select()
        .single();

      if (error) {
        console.error("Error applying enrichment data to lead:", error);
        throw error;
      }
      return data as Lead;
    },
    onSuccess: (data, variables) => {
      toast.success("Lead details updated with enrichment data.");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead", variables.leadId] });
    },
    onError: (error) => {
      toast.error(`Failed to apply enrichment data: ${error.message}`);
    },
  });
}

export function useUpdateLeadFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, fieldsToUpdate }: { leadId: string; fieldsToUpdate: Partial<Lead> }) => {
      console.log(`Updating lead ${leadId} with fields:`, fieldsToUpdate);
      const dataToUpdate = {
        ...fieldsToUpdate,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("lead_master")
        .update(dataToUpdate)
        .eq("id", leadId)
        .select()
        .single();

      if (error) {
        console.error("Error updating lead fields:", error);
        throw error;
      }
      return data as Lead;
    },
    onSuccess: (data, variables) => {
      toast.success("Lead details updated successfully.");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["lead", variables.leadId] });
    },
    onError: (error) => {
      toast.error(`Failed to update lead: ${error.message}`);
    },
  });
}
